/* eslint-disable @typescript-eslint/member-ordering */

import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  EffectRef,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  signal,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatBadge } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterModule } from '@angular/router';
import { CustomerModel } from '@libs/account/models';
import { AppConfigModel } from '@libs/cms/models';
import { FlyoutComponent, FlyoutItemModel, FlyoutModel, ImageComponent, LanguageNavigationComponent, OverlayBoundaryDirective } from '@libs/common/components';
import { ImageItemModel } from '@libs/common/models';
import { IconComponent } from '@libs/components/icons';
import { TextInputModel } from '@libs/form/models';
import { AuthService } from '@libs/shared/auth';
import { FlashMessageService, SpinnerOverlayService } from '@libs/shared/common';
import { CapabilityService } from '@libs/shared/services';
import { NavItemModel } from '@libs/shared/models';
import { AppConfigService, CartService, NavigationService, PageViewService, ViewportService } from '@libs/shared/services';
import { LinkDirective } from '@libs/shared/ui';
import { CatalogSearchInputComponent, CustomerChangeBarComponent } from '@libs/shop/components';
import { firstValueFrom, of } from 'rxjs';

@Component({
  selector: 'ui-cep-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FlyoutComponent,
    MatButtonModule,
    CatalogSearchInputComponent,
    NgTemplateOutlet,
    RouterModule,
    MatBadge,
    IconComponent,
    CustomerChangeBarComponent,
    ImageComponent,
    LanguageNavigationComponent,
    LinkDirective,
    OverlayBoundaryDirective,
  ],
  standalone: true,
})
export class HeaderComponent {
  private readonly viewportService: ViewportService = inject(ViewportService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly cartService: CartService = inject(CartService);
  private readonly navigationService: NavigationService = inject(NavigationService);
  private readonly spinnerOverlay: SpinnerOverlayService = inject(SpinnerOverlayService);
  private readonly flashMessage: FlashMessageService = inject(FlashMessageService);
  private readonly router: Router = inject(Router);
  private readonly capabilityService: CapabilityService = inject(CapabilityService);
  private readonly appConfigService: AppConfigService = inject(AppConfigService);
  private readonly pageViewService: PageViewService = inject(PageViewService);

  public searchTextValue: InputSignal<string> = input('');

  public userNav: Signal<NavItemModel | undefined> = toSignal(this.navigationService.getUserNavigation());
  public ordersNav: Signal<NavItemModel | undefined> = toSignal(this.navigationService.getOrdersNavigation());
  public isDesktopXl: Signal<boolean> = toSignal(this.viewportService.isDesktopXl(), { initialValue: false });
  public authorized: Signal<boolean> = toSignal(this.authService.isUserAuthorized$(), { initialValue: false });
  public isAgentAuthorized: Signal<boolean> = toSignal(this.authService.isUserAuthorized$('agent'), { initialValue: false });
  public currentCustomer: Signal<CustomerModel | undefined> = this.authService.currentCustomer;
  public customers: Signal<CustomerModel[] | undefined> = this.authService.allCustomers;
  public customersLength: Signal<number> = computed(() => this.customers()?.length ?? 0);

  public showHeaderActions: Signal<boolean> = this.capabilityService.isAvailable('account');
  public enableShop: Signal<boolean> = this.capabilityService.isAvailable('shop');
  public showSearch: Signal<boolean> = computed(() => this.authorized() && this.enableShop());
  public showShopNavigation: Signal<boolean> = computed(() => this.isDesktopXl() && this.enableShop());

  public displayInCartCount: Signal<string> = this.enableShop()
    ? toSignal(this.cartService.getCartItemsCountView(), { initialValue: '0' })
    : signal('0');

  public showLanguageSwitch: Signal<boolean> = toSignal(
    this.pageViewService.hasMultipleAvailableLanguages(),
    { initialValue: false },
  );

  private readonly appConfig: Signal<AppConfigModel | undefined> = toSignal(
    this.capabilityService.isAvailable('platform.cmsAppConfigMode')()
      ? this.appConfigService.getConfig()
      : of(undefined),
  );

  public headerLogo: Signal<ImageItemModel | undefined> = computed(() =>
      this.appConfig()?.header.logo ?? {
        type: 'image',
        src: './assets/img/lekkerland24-logo.svg',
        alternateText: 'Lekkerland logo',
        source: [],
        caption: null,
        name: null,
        focalPoint: null,
        lazy: false,
      },
  );

  public showUserFlyout: Signal<boolean> = computed(() => this.authorized());

  public searchSubmit: OutputEmitterRef<string> = output<string>();
  public barcodeClicked: OutputEmitterRef<void> = output<void>();
  public searchFieldClicked: OutputEmitterRef<void> = output<void>();

  public textInputConfig: TextInputModel = {
    type: 'text',
    value: '',
    name: 'search',
    label: 'Sortiment durchsuchen',
    prefixIcon: 'magnifying-glass',
    prefixIconStyle: 'light',
  };

  public userFlyoutConfig: Signal<FlyoutModel> = computed(() => ({
    name: 'Mein Konto',
    icon: this.isAgentAuthorized() ? 'user-secret' : 'user-check',
    items: this.createFlyoutItems(this.userNav()),
  }));

  public orderManagerFlyoutConfig: Signal<FlyoutModel> = computed((): FlyoutModel => ({
    name: 'Bestellmanager',
    icon: 'clipboard-list',
    items: this.createFlyoutItems(this.ordersNav()),
  }));

  private readonly syncSearchTextEffect: EffectRef = effect((): void => {
    this.textInputConfig = {
      ...this.textInputConfig,
      value: this.searchTextValue(),
    };
  });

  public onUserLogin(): void {
    void firstValueFrom(this.authService.login());
  }

  public switchCustomer(customer: CustomerModel): void {
    this.spinnerOverlay.showLoader();

    void this.authService.changeCustomer(customer)
      .then(() => {
        this.flashMessage.showMessage('successCustomerSwitch');

        void this.router.navigateByUrl('');
      })
      .catch(() => void this.flashMessage.showMessage('errorCustomerSwitch'))
      .finally(() => void this.spinnerOverlay.hideLoader());
  }

  private createFlyoutItems(nav?: NavItemModel): FlyoutItemModel[] {
    if (!nav?.children) {
      return [];
    }

    return nav.children
      .filter(item => {
        if (this.customersLength() !== 1) {
          return true;
        }

        return item.name !== 'switch-customer';
      })
      .map(item => ({
        icon: item.icon?.name,
        label: item.label,
        target: item.target,
        url: item.url,
      }));
  }
}
